'use client';
import { CardItemEntity } from '@entities/desktop/cards';
import { SidebarFavoritesEntity } from '@entities/sidebar';
import { useQueryString } from '@hooks/client';
import { useProducts } from '@hooks/queries/use-query-products';
import { ContentWidget, ListCardsWidget } from '@widgets/desktop';
import {
  getPopularsQueryFilters,
  POPULAR_QUERY_KEY,
} from '@widgets/desktop/list-cards-wrappers/popular-items';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/navigation';
import React from 'react';
import { useMediaQuery } from 'usehooks-ts';

import { SeeAllButton } from '@/shared/components/see-all-button';
import { useFavorite } from '@/shared/hooks/queries/use-query-favorite';
import { Illustration, Label } from '@/shared/ui';

import styles from './cards.module.scss';

export const FavoritesCards = observer(() => {
  const router = useRouter();
  const { mappedParams } = useQueryString();
  const { items, isLoading } = useFavorite(mappedParams);
  const {
    isLoading: isPopularLoading,
    items: popularItems,
    isFetching,
  } = useProducts({
    getQueryFiltersFn: getPopularsQueryFilters,
    uniqueQueryKey: POPULAR_QUERY_KEY,
  });

  const isLargeScreen = useMediaQuery('(min-width: 1920px)');

  const slicesItems = isLargeScreen
    ? popularItems.slice(0, 10)
    : popularItems.slice(0, 8);

  return (
    <>
      <ContentWidget
        items={items}
        isLoading={isLoading}
        emptySlot={
          <SidebarFavoritesEntity
            className={styles.empty}
            topSlot={
              <SidebarFavoritesEntity.Image variant={'favorite-no-items'} />
            }
            middleSlot={
              <SidebarFavoritesEntity.Info
                info={{
                  title: 'Favorites are empty',
                  subtitle:
                    'Add items using the heart️ button on the item card!',
                }}
              />
            }
            bottomSlot={
              <SidebarFavoritesEntity.ActionButton
                text={'Open categories'}
                onClick={() => router.push('/categories')}
                variant={'primary'}
                iconRight={
                  <Illustration
                    id={'sidebar-fav-icon'}
                    spriteName={'icons'}
                    name={'square-arrow-right'}
                  />
                }
                iconLeft={
                  <Illustration
                    id={'sidebar-fav-icon'}
                    spriteName={'icons'}
                    name={'widget'}
                  />
                }
              />
            }
          />
        }
      />
      {!isLoading && items?.length === 0 && (
        <ListCardsWidget
          className={styles['list-cards']}
          skeletonsToRender={slicesItems?.length}
          cardsVariant={'tertiary'}
          topSlot={
            <>
              <Label
                labelVariant={'yellow'}
                icon={'graph-up'}
                variant={'primary'}
              >
                Popular Items
              </Label>{' '}
              <SeeAllButton href={'/shop?sort=popular'} />
            </>
          }
          isLoading={isFetching}
          bottomSlot={
            <>
              {slicesItems.map((item, index) => (
                <CardItemEntity
                  applyHandleClickOnFullCard
                  isMM2Variant={false}
                  variant={'tertiary'}
                  key={`card-${index}-${item.realName}-${item?.name}`}
                  item={item}
                />
              ))}
            </>
          }
        />
      )}
    </>
  );
});
